/**
 * The training course (ROUND7 §5; 08 §4 "interactive field manual"): a hand-built map, so every trainee walks
 * the same ground and the coach can point at things that are always where it says. Temperate, day, clear,
 * threat 1, an hour on the clock, a light wind from the north for the dog lesson (ROUND8 §5).
 *
 * Nothing on it can kill him (ROUND7 §5). Its four men are `unarmed`: they see, hear, shout and chase, and
 * never fire or throw; the dog never bites (ROUND8). The one hazard is the stream, and it wounds only a man who
 * lies in it past his held breath - the coach says so the moment he goes under (`src/app/training`).
 * And nothing on it may bring the garrison to alarm 3, because alarm 3 sends armed reinforcements (`mission.ts`,
 * `ALERT`). The ways there, and why none is open here:
 *   - a runner reaching an `alarmPost` (`errands.ts`): the course has none;
 *   - sustained gunfire heard by a soldier (`alert.ts`): the range and the wall are out of every post's
 *     earshot (layout.ts); the dog team cannot follow him over the stream (no ford), and all the ground south
 *     of it is out of earshot too; nothing else on the course fires;
 *   - the dog's bark is a shout, which sends men to look and never raises the alert (ROUND8 §1);
 *   - Hot Extraction, `cellFoundEmpty`: no modifiers, no timed events;
 *   - the Osprey leaving without him, or the trainee emptying magazines beside a live sentry: the sim would
 *     sound the alarm, and the coach ends the course first - the first wave is 25 s behind it
 *     (`src/app/training`).
 * A found body or a searchlight raises the alert to 1 at most; a blast or a shot someone hears, to 2.
 */

import { TRAINING_PARAMS } from '../../meta/training';
import { PI } from '../../sim/math';
import type { MissionDef, SpawnDef, WaterBarrierDef } from '../../sim/types';
import { COURSE, COURSE_IDS, type Post } from './layout';
import { courseProps } from './props';

export { COURSE, COURSE_IDS } from './layout';
export type { Point, Post } from './layout';

/** A man with nothing in his hands, holding his post and looking one way. */
function sentry(post: Post): SpawnDef {
  return { kind: 'rifleman', x: post.x, z: post.z, facing: post.facing, unarmed: true };
}

/** Index in `courseSpawns()` of the dog handler; the dog follows him. */
export const HANDLER_INDEX = 3;

/** The dog team (ROUND8 §5): an unarmed handler walking his beat, and his dog at heel a pace behind. */
function dogTeam(): SpawnDef[] {
  const h = COURSE.dogs.handler;
  const beat = COURSE.dogs.beat.map(([x, z]): [number, number] => [x, z]);
  return [
    { ...sentry(h), patrol: beat },
    { kind: 'dog', x: h.x - 1, z: h.z + 1, facing: h.facing, handler: HANDLER_INDEX },
  ];
}

/**
 * The spawns in order: knife sentry A, knife sentry B, the watcher, the dog handler, his dog (their enemy ids are
 * 1000 to 1004).
 */
export function courseSpawns(): SpawnDef[] {
  return [sentry(COURSE.knife.a), sentry(COURSE.knife.b), sentry(COURSE.watcher), ...dogTeam()];
}

/** The stream (ROUND8 §5): open water from edge to edge, and no crossing - soldiers cannot wade it; he can. */
export function courseStream(): WaterBarrierDef {
  const s = COURSE.dogs.stream;
  return {
    course: { points: s.points.map(([x, z]): [number, number] => [x, z]), width: s.width },
    surface: 'water',
    weakIce: [],
    crossings: [],
  };
}

export function trainingCourse(): MissionDef {
  const t = COURSE.trench;
  return {
    name: 'FIELD TRAINING COURSE',
    archetype: TRAINING_PARAMS.archetype,
    seed: TRAINING_PARAMS.seed,
    biome: 'temperate',
    timeOfDay: 'day',
    weather: 'clear',
    sizeX: COURSE.sizeX,
    sizeZ: COURSE.sizeZ,
    clockSeconds: COURSE.clockSeconds,
    threat: 1,
    startAlert: 0,
    modifiers: [],
    intel: [],
    trenches: [{ points: [[t.x, t.z1], [t.x, t.z0]], width: t.width, depth: t.depth }],
    roads: [],
    ponds: [],
    waterBarriers: [courseStream()],
    mineFields: [],
    props: courseProps(),
    spawns: courseSpawns(),
    captives: [],
    objective: {
      kind: 'destroyTargets',
      label: 'BREACH THE PRACTICE WALL',
      targetPropIds: [COURSE_IDS.wallTarget],
      minFraction: 1,
      rules: [{ type: 'recall', mode: 'normal' }],
    },
    player: { x: COURSE.start.x, z: COURSE.start.z, facing: PI },
    dropBand: { ...COURSE.dropBand },
    wind: { ...COURSE.wind },
    pickup: { ...COURSE.pickup },
    goalZone: { ...COURSE.goalZone },
  };
}
